import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send, CheckCircle2, User as UserIcon, Package } from "lucide-react";
import { toast } from "sonner";

export default function SupportConsole() {
  const { user } = useAuth();
  const [chats, setChats] = useState([]);
  const [active, setActive] = useState(null);
  const [profile, setProfile] = useState(null);
  const [text, setText] = useState("");
  const scrollRef = useRef(null);
  const pollRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/support/chats");
    setChats(data);
    if (active) {
      const fresh = data.find((c) => c.id === active.id);
      if (fresh) setActive(fresh);
    }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 4000);
    // Heartbeat so customers know an agent is online
    const beat = () => api.post("/support/heartbeat").catch(() => {});
    beat();
    const beatInterval = setInterval(beat, 30000);
    return () => {
      clearInterval(pollRef.current);
      clearInterval(beatInterval);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [active]);

  // Load customer profile + recent orders when active chat has a user_id
  useEffect(() => {
    if (active?.user_id) {
      api.get(`/admin/customers/${active.user_id}`).then((r) => setProfile(r.data)).catch(() => setProfile(null));
    } else {
      setProfile(null);
    }
  }, [active?.id, active?.user_id]);

  const claim = async (sid) => {
    try {
      await api.post(`/support/chats/${sid}/claim`);
      toast.success("Chat claimed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to claim");
    }
  };

  const send = async () => {
    if (!text.trim() || !active) return;
    await api.post(`/support/chats/${active.id}/message`, { text: text.trim() });
    setText("");
    load();
  };

  const resolve = async () => {
    if (!active) return;
    await api.post(`/support/chats/${active.id}/resolve`);
    toast.success("Marked resolved");
    setActive(null);
    load();
  };

  return (
    <main className="bg-[#fdfbf7] min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <div className="overline">Stylist console</div>
        <h1 className="font-serif text-5xl mt-3 mb-10">Live conversations</h1>

        <div
          className={`grid ${
            profile ? "lg:grid-cols-[300px_1fr_320px]" : "lg:grid-cols-[340px_1fr]"
          } gap-0 h-[68vh] border border-[#ebe5df] bg-white`}
        >
          {/* Queue */}
          <div className="border-r border-[#ebe5df] overflow-y-auto">
            <div className="px-4 py-3 text-xs uppercase tracking-[0.18em] text-[#5c5a56] border-b border-[#ebe5df]">
              {chats.length} live · signed in as {user?.name}
            </div>
            {chats.length === 0 && (
              <div className="p-6 text-sm text-[#5c5a56]">No one's waiting right now ✦</div>
            )}
            {chats.map((c) => {
              const last = (c.messages || []).filter((m) => m.role !== "system").slice(-1)[0];
              const isMine = c.claimed_by === user.id;
              const isFree = !c.claimed_by;
              return (
                <button
                  key={c.id}
                  onClick={() => setActive(c)}
                  className={`w-full text-left px-4 py-3 border-b border-[#ebe5df] hover:bg-[#fdfbf7] ${
                    active?.id === c.id ? "bg-[#f3efea]" : ""
                  }`}
                  data-testid={`chat-row-${c.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{c.guest_name}</span>
                    <span
                      className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 ${
                        isFree
                          ? "bg-[#c58b76] text-white"
                          : isMine
                          ? "bg-[#365e47] text-white"
                          : "bg-[#ebe5df] text-[#5c5a56]"
                      }`}
                    >
                      {isFree ? "open" : isMine ? "you" : c.claimed_by_name || "claimed"}
                    </span>
                  </div>
                  <div className="text-xs text-[#5c5a56] mt-1 truncate">
                    {last?.text || "—"}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Conversation */}
          <div className="flex flex-col">
            {!active ? (
              <div className="flex-1 flex items-center justify-center text-[#5c5a56]">
                Select a conversation
              </div>
            ) : (
              <>
                <div className="px-5 py-3 border-b border-[#ebe5df] flex items-center justify-between">
                  <div>
                    <div className="font-serif text-xl">{active.guest_name}</div>
                    <div className="overline">{active.status}</div>
                  </div>
                  <div className="flex gap-2">
                    {!active.claimed_by && (
                      <button
                        onClick={() => claim(active.id)}
                        className="btn-primary"
                        data-testid="claim-chat"
                      >
                        Claim
                      </button>
                    )}
                    {active.claimed_by && active.status !== "resolved" && (
                      <button
                        onClick={resolve}
                        className="btn-ghost"
                        data-testid="resolve-chat"
                      >
                        <CheckCircle2 size={14} /> Resolve
                      </button>
                    )}
                  </div>
                </div>
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3 bg-[#fdfbf7]">
                  {(active.messages || []).map((m, i) => (
                    <div
                      key={i}
                      className={`flex ${m.role === "agent" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[70%] px-3 py-2 text-sm ${
                          m.role === "agent"
                            ? "bg-[#1a1918] text-white"
                            : m.role === "system"
                            ? "italic text-xs text-[#5c5a56] w-full text-center bg-transparent"
                            : "bg-white border border-[#ebe5df]"
                        }`}
                      >
                        {m.text}
                      </div>
                    </div>
                  ))}
                </div>
                {active.claimed_by === user.id && (
                  <div className="border-t border-[#ebe5df] p-3 flex gap-2">
                    <input
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && send()}
                      placeholder="Reply to the customer…"
                      className="flex-1 border border-[#ebe5df] px-3 py-2 text-sm focus:outline-none focus:border-[#c58b76]"
                      data-testid="agent-input"
                    />
                    <button
                      onClick={send}
                      className="bg-[#1a1918] text-white w-10 h-10 flex items-center justify-center hover:bg-[#c58b76]"
                      data-testid="agent-send"
                    >
                      <Send size={16} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Customer profile panel */}
          {profile && (
            <div className="border-l border-[#ebe5df] overflow-y-auto p-5 bg-[#fdfbf7]" data-testid="support-customer-panel">
              <div className="flex items-center gap-2 overline mb-3">
                <UserIcon size={14} /> Customer
              </div>
              <div className="font-serif text-2xl">{profile.customer.name}</div>
              <div className="text-sm text-[#5c5a56] mt-1">{profile.customer.email}</div>
              <div className="text-sm text-[#5c5a56]">{profile.customer.phone || "no phone"}</div>

              <div className="grid grid-cols-2 gap-3 mt-5">
                <div className="border border-[#ebe5df] bg-white p-3">
                  <div className="overline">Orders</div>
                  <div className="font-serif text-2xl mt-1">{profile.orders_count}</div>
                </div>
                <div className="border border-[#ebe5df] bg-white p-3">
                  <div className="overline">Spent</div>
                  <div className="font-serif text-xl mt-1">
                    ₹{(profile.total_spent || 0).toLocaleString("en-IN")}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 overline mt-6 mb-2">
                <Package size={14} /> Recent orders
              </div>
              {profile.orders.length === 0 ? (
                <div className="text-sm text-[#5c5a56]">No orders.</div>
              ) : (
                <div className="space-y-2">
                  {profile.orders.slice(0, 6).map((o) => (
                    <div key={o.id} className="border border-[#ebe5df] bg-white p-2.5 text-sm" data-testid={`support-order-${o.id}`}>
                      <div className="flex justify-between items-baseline">
                        <span className="font-mono text-xs">#{o.id.slice(0, 8)}</span>
                        <span className="text-[10px] uppercase tracking-widest text-[#5c5a56]">
                          {o.status?.replace(/_/g, " ")}
                        </span>
                      </div>
                      <div className="mt-1 text-xs">
                        {o.items?.map((i) => `${i.name} ×${i.quantity}`).join(" · ")}
                      </div>
                      <div className="mt-1 flex justify-between items-center">
                        <span className="font-serif text-base">₹{(o.total || 0).toLocaleString("en-IN")}</span>
                        {o.tracking_number && (
                          <span className="text-[10px] text-[#5c5a56]">{o.courier} {o.tracking_number}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
